/**
 * PointAssociationSection - Sección de asociación a red para cualquier Point Entity
 *
 * @remarks
 * Componente genérico que se inserta en cualquier panel de detalles
 * para mostrar y gestionar la asociación de una entidad punto con la red.
 * Soporta conexiones múltiples.
 *
 * @see ARQUITECTURA_ASOCIACION_PUNTO_RED.md
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Network,
  RefreshCw,
  Lock,
  Unlock,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  HelpCircle,
  Zap,
  Star,
  Trash2,
  Plus,
  ChevronDown,
  ChevronUp,
  MapPin,
  GitBranch,
  Crosshair,
  CircleX,
} from 'lucide-react';
import { usePointAssociation } from '../hooks/usePointAssociation';
import { usePointAssociationVisualization } from '../hooks/usePointAssociationVisualization';
import { useAssociationMapSelection, type AssociationMapTarget } from '../hooks/useAssociationMapSelection';
import { getPointEntityConfig } from '../config/point-entities.config';
import type { NetworkPointAssociation, AssociationStatus, AssociationCandidate } from '../services/point-association-api.service';
import './point-association-section.css';

export interface PointAssociationSectionProps {
  /** ID del proyecto */
  projectId: string;
  /** Tipo de entidad */
  entityType: string;
  /** ID de la entidad */
  entityId: string;
  /** Coordenadas actuales de la entidad [lng, lat] */
  coordinates: [number, number];
  /** Callback cuando la asociación cambia */
  onAssociationChange?: (association: NetworkPointAssociation | null) => void;
  /** Modo compacto para popups */
  compact?: boolean;
}

/**
 * Obtiene el icono de estado
 */
function StatusIcon({ status }: { status: AssociationStatus }): JSX.Element {
  switch (status) {
    case 'valid':
      return <CheckCircle2 size={12} className="status-icon valid" />;
    case 'weak':
      return <Zap size={12} className="status-icon weak" />;
    case 'needs_review':
      return <AlertCircle size={12} className="status-icon needs-review" />;
    case 'needs_recalculation':
    case 'recalculated':
      return <RefreshCw size={12} className="status-icon pending" />;
    case 'manual_lock':
      return <Lock size={12} className="status-icon locked" />;
    case 'orphaned':
      return <AlertTriangle size={12} className="status-icon orphaned" />;
    default:
      return <HelpCircle size={12} className="status-icon unknown" />;
  }
}

/**
 * Obtiene la etiqueta de lado
 */
function getSideLabel(side: string | undefined): string {
  switch (side) {
    case 'left':
      return 'Izq';
    case 'right':
      return 'Der';
    default:
      return '';
  }
}

/**
 * Item de conexión individual
 */
function ConnectionItem({
  connection,
  isPrimary,
  canRemove,
  allowsLock,
  isLoading,
  onSetPrimary,
  onLock,
  onUnlock,
  onRemove,
}: {
  connection: NetworkPointAssociation;
  isPrimary: boolean;
  canRemove: boolean;
  allowsLock: boolean;
  isLoading: boolean;
  onSetPrimary: () => void;
  onLock: () => void;
  onUnlock: () => void;
  onRemove: () => void;
}): JSX.Element {
  const isEdge = connection.associationType === 'edge';
  const isLocked = connection.isLocked || connection.status === 'manual_lock';
  const isManual = connection.method === 'manual';
  const isOrphaned = connection.status === 'orphaned';
  const withinThreshold = connection.withinThreshold !== false;
  const targetLabel = isEdge ? connection.edgeId : connection.nodeId;
  const positionPercent = isEdge && connection.positionOnEdge !== null
    ? `${(connection.positionOnEdge * 100).toFixed(1)}%`
    : null;

  return (
    <div className={`connection-item ${isPrimary ? 'primary' : ''} ${isOrphaned ? 'orphaned' : ''} ${!withinThreshold ? 'outside-threshold' : ''}`}>
      {/* Info principal */}
      <div className="connection-info">
        <div className="connection-type">
          {isPrimary && <span title="Conexión primaria"><Star size={12} className="primary-star" /></span>}
          {isEdge ? <GitBranch size={14} /> : <MapPin size={14} />}
          <span className="type-label">{isEdge ? 'Segmento' : 'Nodo'}</span>
        </div>

        <div className="connection-details">
          <span className="distance">
            {Number(connection.distanceToNetwork ?? 0).toFixed(1)}m
          </span>
          {positionPercent && (
            <span className="position">{positionPercent}</span>
          )}
          {isEdge && connection.sideOfEdge && (
            <span className="side">{getSideLabel(connection.sideOfEdge)}</span>
          )}
          {isManual && <span className="method-badge manual">manual</span>}
          {!withinThreshold && <span className="method-badge warning">fuera umbral</span>}
        </div>

        {targetLabel && (
          <div className="connection-target">
            {targetLabel}
          </div>
        )}

        {(connection.projectedCoordinate || connection.projectionType) && (
          <div className="connection-metadata">
            {connection.projectionType && (
              <span>{connection.projectionType}</span>
            )}
            {connection.projectedCoordinate && (
              <span>
                {connection.projectedCoordinate[0].toFixed(5)}, {connection.projectedCoordinate[1].toFixed(5)}
              </span>
            )}
          </div>
        )}

        <div className="connection-status">
          <StatusIcon status={connection.status} />
        </div>
      </div>

      {/* Acciones */}
      <div className="connection-actions">
        {!isPrimary && (
          <button
            className="action-btn set-primary"
            onClick={onSetPrimary}
            disabled={isLoading}
            title="Hacer primaria"
          >
            <Star size={12} />
          </button>
        )}

        {allowsLock && (
          <button
            className={`action-btn ${isLocked ? 'locked' : ''}`}
            onClick={isLocked ? onUnlock : onLock}
            disabled={isLoading}
            title={isLocked ? 'Desbloquear' : 'Bloquear'}
          >
            {isLocked ? <Lock size={12} /> : <Unlock size={12} />}
          </button>
        )}

        {canRemove && (
          <button
            className="action-btn remove"
            onClick={onRemove}
            disabled={isLoading}
            title="Eliminar conexión secundaria"
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Item de candidato para agregar
 */
function CandidateItem({
  candidate,
  isLoading,
  onAdd,
}: {
  candidate: AssociationCandidate;
  isLoading: boolean;
  onAdd: () => void;
}): JSX.Element {
  const isEdge = candidate.type === 'edge';

  return (
    <div className={`candidate-item ${!candidate.withinThreshold ? 'outside-threshold' : ''}`}>
      <div className="candidate-info">
        <div className="candidate-type">
          {isEdge ? <GitBranch size={14} /> : <MapPin size={14} />}
          <span className="type-label">{isEdge ? 'Segmento' : 'Nodo'}</span>
        </div>
        <div className="candidate-details">
          <span className="distance">{candidate.distance.toFixed(1)}m</span>
          {isEdge && candidate.sideOfEdge && (
            <span className="side">{getSideLabel(candidate.sideOfEdge)}</span>
          )}
          {!candidate.withinThreshold && (
            <span className="method-badge warning">fuera umbral</span>
          )}
        </div>
      </div>
      <button
        className="action-btn add"
        onClick={onAdd}
        disabled={isLoading}
        title="Agregar conexión"
      >
        <Plus size={14} />
      </button>
    </div>
  );
}

/**
 * Componente principal
 */
export function PointAssociationSection({
  projectId,
  entityType,
  entityId,
  coordinates,
  onAssociationChange,
  compact = false,
}: PointAssociationSectionProps): JSX.Element | null {
  const config = getPointEntityConfig(entityType);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showCandidates, setShowCandidates] = useState(false);
  const [mapSelectionMode, setMapSelectionMode] = useState<'replace' | 'add' | null>(null);
  const [pendingMapTarget, setPendingMapTarget] = useState<AssociationMapTarget | null>(null);
  const [message, setMessage] = useState<{ type: 'info' | 'success' | 'error'; text: string } | null>(null);

  const {
    association,
    allAssociations,
    candidates,
    isLoading,
    isLoadingCandidates,
    error,
    allowsMultipleConnections,
    recalculate,
    lockConnection,
    unlockConnection,
    addConnection,
    replacePrimary,
    removeConnection,
    setPrimary,
    loadCandidates,
    clearCandidates,
  } = usePointAssociation(projectId, entityType, entityId, coordinates);

  // Activar visualización en mapa (todas las conexiones)
  usePointAssociationVisualization({
    associations: allAssociations,
    sourceCoordinates: coordinates,
    visible: true,
  });

  const currentPrimaryTarget = useMemo<AssociationMapTarget | null>(() => {
    if (!association || association.associationType === 'none') {
      return null;
    }

    const id = association.associationType === 'edge' ? association.edgeId : association.nodeId;
    if (!id) {
      return null;
    }

    return {
      type: association.associationType,
      id,
    };
  }, [association]);

  useAssociationMapSelection({
    enabled: !!mapSelectionMode,
    allowNodes: true,
    selectedTarget: pendingMapTarget ?? currentPrimaryTarget,
    onPick: (target) => {
      setPendingMapTarget(target);
      setMessage({
        type: 'info',
        text: `${target.type === 'edge' ? 'Segmento' : 'Nodo'} seleccionado. Confirmá la operación para aplicar el cambio.`,
      });
    },
    onInvalidPick: () => {
      setMessage({
        type: 'error',
        text: 'No se encontró un segmento o nodo válido para reasociar en ese punto.',
      });
    },
  });

  useEffect(() => {
    if (onAssociationChange) {
      onAssociationChange(association);
    }
  }, [association, onAssociationChange]);

  useEffect(() => {
    if (!error) {
      return;
    }

    setMessage({
      type: 'error',
      text: error.message,
    });
  }, [error]);

  if (!config) {
    return null;
  }

  // Handlers
  const handleRecalculate = async (): Promise<void> => {
    await recalculate();
  };

  const handleToggleCandidates = async (): Promise<void> => {
    setMapSelectionMode(null);
    setPendingMapTarget(null);
    if (showCandidates) {
      clearCandidates();
      setShowCandidates(false);
    } else {
      // Radio amplio para selección manual (500m) - sin restricción práctica
      await loadCandidates({ maxDistance: 500, includeNodes: true });
      setShowCandidates(true);
    }
  };

  const handleAddCandidate = async (candidate: AssociationCandidate): Promise<void> => {
    if (allowsMultipleConnections && hasAnyConnection) {
      const result = await addConnection({ type: candidate.type, id: candidate.id });
      if (result) {
        setMessage({ type: 'success', text: 'Asociación secundaria agregada.' });
      }
    } else {
      const result = await replacePrimary({ type: candidate.type, id: candidate.id });
      if (result) {
        setMessage({ type: 'success', text: 'Asociación primaria actualizada.' });
      }
    }
    setShowCandidates(false);
  };

  const handleStartMapSelection = (mode: 'replace' | 'add'): void => {
    clearCandidates();
    setShowCandidates(false);
    setPendingMapTarget(null);
    setMapSelectionMode(mode);
    setMessage({
      type: 'info',
      text: mode === 'add'
        ? 'Seleccioná en el mapa el segmento o nodo que querés agregar como asociación secundaria.'
        : 'Seleccioná en el mapa el segmento o nodo que querés usar como nueva asociación primaria.',
    });
  };

  const handleCancelMapSelection = (): void => {
    setPendingMapTarget(null);
    setMapSelectionMode(null);
    setMessage({
      type: 'info',
      text: 'Operación cancelada sin cambios.',
    });
  };

  const handleConfirmMapSelection = async (): Promise<void> => {
    if (!pendingMapTarget) {
      return;
    }

    if (mapSelectionMode === 'add') {
      const result = await addConnection(pendingMapTarget);
      if (!result) {
        return;
      }
      setMessage({ type: 'success', text: 'Asociación secundaria agregada.' });
    } else {
      const result = await replacePrimary(pendingMapTarget);
      if (!result) {
        return;
      }
      setMessage({ type: 'success', text: 'Asociación primaria actualizada.' });
    }

    setPendingMapTarget(null);
    setMapSelectionMode(null);
  };

  const hasMultipleConnections = allAssociations.length > 1;
  const hasAnyConnection = allAssociations.length > 0 &&
    allAssociations.some(a => a.associationType !== 'none');
  const candidateActionLabel = allowsMultipleConnections && hasAnyConnection
    ? 'Agregar'
    : hasAnyConnection
      ? 'Reasociar'
      : 'Manual';

  // Modo compacto: solo mostrar estado básico
  if (compact) {
    return (
      <div className="point-association-section compact">
        <div className="section-header">
          <div className="header-title">
            <Network size={14} />
            <span>Red</span>
          </div>
          {association && association.associationType !== 'none' ? (
            <div className="compact-info">
              <span className="distance">{Number(association.distanceToNetwork ?? 0).toFixed(1)}m</span>
              <StatusIcon status={association.status} />
            </div>
          ) : (
            <span className="no-connection">Sin conexión</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="point-association-section">
      {/* Header */}
      <div className="section-header">
        <div className="header-title">
          <Network size={16} />
          <span>Asociación a Red</span>
          {hasMultipleConnections && (
            <span className="connection-count">({allAssociations.length})</span>
          )}
        </div>
        <div className="header-actions">
          {hasMultipleConnections && (
            <button
              className="expand-btn"
              onClick={() => setIsExpanded(!isExpanded)}
              title={isExpanded ? 'Colapsar' : 'Expandir'}
            >
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="section-content">
        {isLoading && allAssociations.length === 0 ? (
          <div className="loading-state">
            <RefreshCw size={16} className="spin" />
            <span>Cargando...</span>
          </div>
        ) : hasAnyConnection ? (
          <>
            {/* Lista de conexiones */}
            <div className="connections-list">
              {(isExpanded || !hasMultipleConnections ? allAssociations : [association!])
                .filter(a => a && a.associationType !== 'none')
                .map((conn) => {
                  const effectiveIsPrimary = conn.isPrimary ?? conn.id === association?.id;

                  return (
                    <ConnectionItem
                      key={conn.id}
                      connection={conn}
                      isPrimary={effectiveIsPrimary}
                      canRemove={!effectiveIsPrimary && allAssociations.length > 1}
                      allowsLock={config.allowsManualLock ?? false}
                      isLoading={isLoading}
                      onSetPrimary={() => setPrimary(conn.id)}
                      onLock={() => lockConnection(conn.id)}
                      onUnlock={() => unlockConnection(conn.id)}
                      onRemove={() => removeConnection(conn.id)}
                    />
                  );
                })}
            </div>

            {/* Mostrar indicador de más conexiones si está colapsado */}
            {!isExpanded && hasMultipleConnections && (
              <button
                className="show-more-btn"
                onClick={() => setIsExpanded(true)}
              >
                +{allAssociations.length - 1} más
              </button>
            )}

            {/* Acciones principales */}
            <div className="section-actions">
              {config.allowsManualReassociation && (
                <button
                  className="action-button recalculate"
                  onClick={handleRecalculate}
                  disabled={isLoading}
                  title="Recalcular asociación automática"
                >
                  <RefreshCw size={14} />
                  <span>Recalcular</span>
                </button>
              )}

              {config.allowsManualReassociation && (
                <button
                  className={`action-button pick-on-map ${mapSelectionMode === 'replace' ? 'active' : ''}`}
                  onClick={() => handleStartMapSelection('replace')}
                  disabled={isLoading || isLoadingCandidates}
                  title="Seleccionar nueva primaria en el mapa"
                >
                  <Crosshair size={14} />
                  <span>Reasociar en mapa</span>
                </button>
              )}

              {config.allowsManualReassociation && allowsMultipleConnections && hasAnyConnection && (
                <button
                  className={`action-button pick-on-map ${mapSelectionMode === 'add' ? 'active' : ''}`}
                  onClick={() => handleStartMapSelection('add')}
                  disabled={isLoading || isLoadingCandidates}
                  title="Agregar secundaria desde el mapa"
                >
                  <Crosshair size={14} />
                  <span>Agregar secundaria</span>
                </button>
              )}

              {config.allowsManualReassociation && (
                <button
                  className={`action-button add-connection ${showCandidates ? 'active' : ''}`}
                  onClick={handleToggleCandidates}
                  disabled={isLoading || isLoadingCandidates}
                  title={`${candidateActionLabel} desde lista`}
                >
                  {isLoadingCandidates ? (
                    <RefreshCw size={14} className="spin" />
                  ) : (
                    <Plus size={14} />
                  )}
                  <span>{candidateActionLabel}</span>
                </button>
              )}
            </div>

            {(message || mapSelectionMode) && (
              <div className={`association-feedback ${message?.type ?? 'info'}`}>
                <div className="association-feedback__text">
                  {mapSelectionMode && !pendingMapTarget && (
                    <span>Modo selección activo. Hacé click sobre un segmento o nodo de la red.</span>
                  )}
                  {message?.text && <span>{message.text}</span>}
                </div>

                {mapSelectionMode && (
                  <div className="association-feedback__actions">
                    {pendingMapTarget && (
                      <button
                        className="action-button confirm-map-selection"
                        onClick={() => { void handleConfirmMapSelection(); }}
                        disabled={isLoading}
                      >
                        {mapSelectionMode === 'add' ? 'Confirmar secundaria' : 'Confirmar primaria'}
                      </button>
                    )}
                    <button
                      className="action-button cancel-map-selection"
                      onClick={handleCancelMapSelection}
                      disabled={isLoading}
                    >
                      <CircleX size={14} />
                      <span>Cancelar</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Panel de candidatos */}
            {showCandidates && (
              <div className="candidates-panel">
                <div className="candidates-header">
                  <span>Segmentos/Nodos cercanos</span>
                  <button
                    className="close-btn"
                    onClick={() => { clearCandidates(); setShowCandidates(false); }}
                  >
                    ×
                  </button>
                </div>
                {candidates.length > 0 ? (
                  <div className="candidates-list">
                    {candidates.slice(0, 10).map((candidate) => (
                      <CandidateItem
                        key={`${candidate.type}-${candidate.id}`}
                        candidate={candidate}
                        isLoading={isLoading}
                        onAdd={() => handleAddCandidate(candidate)}
                      />
                    ))}
                    {candidates.length > 10 && (
                      <div className="candidates-more">
                        +{candidates.length - 10} más disponibles
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="candidates-empty">
                    No hay candidatos disponibles
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="no-association-state">
            <AlertTriangle size={16} />
            <span>Sin asociación a la red</span>
            <div className="no-assoc-actions">
              {config.allowsManualReassociation && (
                <button
                  className="action-button associate"
                  onClick={handleRecalculate}
                  disabled={isLoading}
                >
                  <Network size={14} />
                  <span>Asociar</span>
                </button>
              )}
              {config.allowsManualReassociation && (
                <button
                  className={`action-button pick-on-map ${mapSelectionMode === 'replace' ? 'active' : ''}`}
                  onClick={() => handleStartMapSelection('replace')}
                  disabled={isLoading || isLoadingCandidates}
                >
                  <Crosshair size={14} />
                  <span>Seleccionar en mapa</span>
                </button>
              )}
              {config.allowsManualReassociation && (
                <button
                  className="action-button add-connection"
                  onClick={handleToggleCandidates}
                  disabled={isLoading || isLoadingCandidates}
                >
                  <Plus size={14} />
                  <span>{candidateActionLabel}</span>
                </button>
              )}
            </div>

            {(message || mapSelectionMode) && (
              <div className={`association-feedback ${message?.type ?? 'info'}`}>
                <div className="association-feedback__text">
                  {mapSelectionMode && !pendingMapTarget && (
                    <span>Modo selección activo. Hacé click sobre un segmento o nodo de la red.</span>
                  )}
                  {message?.text && <span>{message.text}</span>}
                </div>
                {mapSelectionMode && (
                  <div className="association-feedback__actions">
                    {pendingMapTarget && (
                      <button
                        className="action-button confirm-map-selection"
                        onClick={() => { void handleConfirmMapSelection(); }}
                        disabled={isLoading}
                      >
                        Confirmar asociación
                      </button>
                    )}
                    <button
                      className="action-button cancel-map-selection"
                      onClick={handleCancelMapSelection}
                      disabled={isLoading}
                    >
                      <CircleX size={14} />
                      <span>Cancelar</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Panel de candidatos (sin conexiones) */}
            {showCandidates && (
              <div className="candidates-panel">
                <div className="candidates-header">
                  <span>Segmentos/Nodos cercanos</span>
                  <button
                    className="close-btn"
                    onClick={() => { clearCandidates(); setShowCandidates(false); }}
                  >
                    ×
                  </button>
                </div>
                {candidates.length > 0 ? (
                  <div className="candidates-list">
                    {candidates.slice(0, 10).map((candidate) => (
                      <CandidateItem
                        key={`${candidate.type}-${candidate.id}`}
                        candidate={candidate}
                        isLoading={isLoading}
                        onAdd={() => handleAddCandidate(candidate)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="candidates-empty">
                    No hay candidatos cercanos
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
